/**
 * Owner: Masa Bayu
 * File: library-progress-intelligence.ts
 * Date:Time: 2026-09-16 06:40
 * Description: T4-E2 continue-reading intelligence + resume intent (≠ QXReader).
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { applyReadingProgress } from './library-item';
import { assertT4E1SecurityIntact } from './library-security';
import { authorizeLibraryReaderLaunch } from './library-reader-launch';
import { assertTimestamp } from './guards';
export function recordLibraryContinueReading(item, options) {
    assertNoForbiddenFoundationKey(options, 'QXRead continue reading');
    assertT4E1SecurityIntact();
    if (typeof options.lastPositionRef !== 'string' || options.lastPositionRef.trim().length === 0) {
        throw new Error('QXRead continue reading requires lastPositionRef');
    }
    const lastOpenedAt = options.lastOpenedAt ?? new Date().toISOString();
    assertTimestamp(lastOpenedAt, 'QXRead continue reading lastOpenedAt');
    return applyReadingProgress(item, {
        state: 'in_progress',
        percentComplete: options.percentComplete,
        lastOpenedAt,
        lastPositionRef: options.lastPositionRef.trim(),
    });
}
export function buildContinueReadingRail(items) {
    return Object.freeze(items
        .filter((item) => item.progress.state === 'in_progress' &&
        item.state !== 'blocked' &&
        item.state !== 'removed' &&
        item.state !== 'archived')
        .slice()
        .sort((left, right) => {
        const leftAt = left.progress.lastOpenedAt ?? '';
        const rightAt = right.progress.lastOpenedAt ?? '';
        return rightAt.localeCompare(leftAt);
    }));
}
/**
 * Resume intent packages progress reference for a future T5 hand-off.
 * Does not open QXReader.
 */
export function createLibraryResumeIntent(input) {
    assertT4E1SecurityIntact();
    const launch = authorizeLibraryReaderLaunch({
        item: input.item,
        entitlement: input.entitlement,
        sessionAuthenticated: input.sessionAuthenticated,
        accountId: input.accountId,
    });
    if (!launch.allowed) {
        return Object.freeze({
            allowed: false,
            reason: launch.reason,
            libraryItemId: input.item.libraryItemId,
            publicationId: input.item.publicationId,
            lastPositionRef: input.item.progress.lastPositionRef,
            percentComplete: input.item.progress.percentComplete,
            lastOpenedAt: input.item.progress.lastOpenedAt,
            executesReader: false,
        });
    }
    if (input.item.progress.state !== 'in_progress' && input.item.progress.state !== 'completed') {
        return Object.freeze({
            allowed: false,
            reason: 'Resume intent requires in-progress or completed progress state',
            libraryItemId: input.item.libraryItemId,
            publicationId: input.item.publicationId,
            lastPositionRef: input.item.progress.lastPositionRef,
            percentComplete: input.item.progress.percentComplete,
            lastOpenedAt: input.item.progress.lastOpenedAt,
            executesReader: false,
        });
    }
    return Object.freeze({
        allowed: true,
        reason: 'Resume intent ready — QXReader execution remains T5',
        libraryItemId: input.item.libraryItemId,
        publicationId: input.item.publicationId,
        lastPositionRef: input.item.progress.lastPositionRef,
        percentComplete: input.item.progress.percentComplete,
        lastOpenedAt: input.item.progress.lastOpenedAt,
        executesReader: false,
    });
}
//# sourceMappingURL=library-progress-intelligence.js.map